import { INotificationMessage } from "@fl/shared/src/types/messages";
import { CertificadoAttributes } from "@fl/shared/src/types/models/suporte_fl/certificados";
import { yupResolver } from "@hookform/resolvers/yup";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { ICertDataModal } from "../../../../pages/private/condominios/certificados";
import { certificadoSchema } from "../../../../schemas/certificados";
import ApiCertificados from "../../../../services/suporte/auth/certificados";
import StringsFuncs from "../../../../utils/stringsFuncs";
import AlterMessages from "../../../alerts/indes";

interface IProps {
  showModal: boolean;
  data: ICertDataModal | null;
  onResultCertificado: (item: any) => void;
}

export type CertificadoFormInputs = Omit<
  CertificadoAttributes,
  "createdAt" | "updatedAt"
> & {
  confirmarSenha: string;
  file: ArrayBuffer | string;
  fileName: string;
};

const CertificadoModal: React.FC<IProps> = ({
  showModal,
  data,
  onResultCertificado,
}) => {
  const [messages, setMessages] = useState<INotificationMessage[]>([]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors },
  } = useForm<CertificadoFormInputs>({
    resolver: yupResolver(certificadoSchema),
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileName = watch("fileName");

  useEffect(() => {
    if (data) {
      reset({
        arquivo: new StringsFuncs().generateSmallCode(),
        id_condominio_cond: data.id_condominio_cond || 0, // Garante que sempre tenha um número válido
      });
    }

    setMessages([]);
  }, [data, showModal]);

  useEffect(() => {
    const validateFields = async () => {
      try {
        await certificadoSchema.validateAt("file", { file: getValues("file") });
        await certificadoSchema.validateAt("arquivo", {
          arquivo: getValues("arquivo"),
        });
        await certificadoSchema.validateAt("senha", {
          senha: getValues("senha"),
        });
        await certificadoSchema.validateAt("confirmarSenha", {
          senha: getValues("senha"),
          confirmarSenha: getValues("confirmarSenha"),
        });
        checkCertificate(getValues("file"));
      } catch (error) {
        setMessages([]);
      }
    };

    validateFields();
  }, [
    watch("file"),
    watch("arquivo"),
    watch("senha"),
    watch("confirmarSenha"),
  ]);

  const onSubmit = handleSubmit((data: CertificadoFormInputs) => {
    onResultCertificado(data);
  });

  const checkCertificate = async (file: ArrayBuffer | string) => {
    const result = await new ApiCertificados({
      senha: getValues("senha"),
      arquivo: getValues("arquivo"),
    }).checkCertificate(file);

    if (result.success) {
      const emissao: any = moment(result.data.emissao).format(
        "YYYY-MM-DDTHH:mm"
      );
      const expiracao: any = moment(result.data.expiracao).format(
        "YYYY-MM-DDTHH:mm"
      );
      setValue("data_emissao", emissao);
      setValue("data_expiracao", expiracao);
    } else {
      const newDate: any = "";

      setValue("data_emissao", newDate);
      setValue("data_expiracao", newDate);
    }
    if (result.message) setMessages(result.message);
  };

  return (
    <Modal
      show={showModal}
      onHide={() => onResultCertificado(null)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {!data ? "" : `Novo Certificado - ${data.condominio}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="container-text text-jsutify">
        <Form>
          <Row className="mt-2">
            <Col className="mt-1">
              <Form.Group controlId="formNome">
                <Form.Label>Nome do Arquivo:</Form.Label>
                <Form.Control
                  disabled
                  type="text"
                  {...register("arquivo")}
                  isInvalid={!!errors.arquivo}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.arquivo?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col className="mt-1">
              <Form.Group controlId="formSenha">
                <Form.Label>Senha:</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Digite a senha"
                  {...register("senha")}
                  isInvalid={!!errors.senha}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.senha?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col className="mt-1">
              <Form.Group controlId="formConfirmarSenha">
                <Form.Label>Confirmar Senha:</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirme a senha"
                  {...register("confirmarSenha")}
                  isInvalid={!!errors.confirmarSenha}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.confirmarSenha?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            {/* Input de Arquivo Personalizado */}
            <Col className="mt-1">
              <Form.Group controlId="formFile">
                <Form.Label>Certificado (PFX):</Form.Label>
                <div className="d-flex align-items-center">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Selecionar Arquivo
                  </Button>
                  <span className="ms-2">
                    {fileName !== "" ? fileName : "Nenhum arquivo selecionado"}
                  </span>
                </div>
                <Form.Control
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="application/pfx"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files && e.target.files[0]) {
                      setValue("fileName", e.target.files[0].name);
                      const reader = new FileReader();
                      reader.readAsDataURL(e.target.files[0]);
                      reader.onload = async (e) => {
                        if (e.target && e.target.result) {
                          setValue("file", e.target.result);
                        }
                      };
                    }
                  }}
                  isInvalid={!!errors.file}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.file?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col className="mt-1">
              <Form.Group controlId="formEmissao">
                <Form.Label>Data de Emissão:</Form.Label>
                <Form.Control
                  disabled
                  type="datetime-local"
                  {...register("data_emissao")}
                  isInvalid={!!errors.data_emissao}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.data_emissao?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col className="mt-1">
              <Form.Group controlId="formExpiracao">
                <Form.Label>Data de Expiração:</Form.Label>
                <Form.Control
                  disabled
                  type="datetime-local"
                  {...register("data_expiracao")}
                  isInvalid={!!errors.data_expiracao}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.data_expiracao?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>

        <AlterMessages
          messages={messages}
          setMessages={setMessages}
          floating={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <div
          style={{ width: "100%" }}
          className="d-flex justify-content-between"
        >
          <Button
            variant="danger"
            type="submit"
            onClick={() => onResultCertificado(null)}
          >
            Cancelar
          </Button>
          <Button variant="success" type="submit" onClick={onSubmit}>
            Confirmar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CertificadoModal;
